import { round } from 'lodash';

import { AppConstants } from '../../../constants';
import { ILoanPaymentAllocation } from '../../../interfaces';

const { MONEY_DECIMAL_PLACES } = AppConstants;

/**
 * An allocation as the form holds it while the user edits.
 *
 * The two amounts are strings because they are bound to number inputs, and an input mid-edit is not
 * always a number -- a half-typed "1." or an emptied field would round-trip through Number as NaN or
 * 0 and fight the user's cursor. They are converted once, on the way out.
 */
interface IAllocationDraft {
  loan: string;
  amount_to_interest: string;
  amount_to_principal: string;
}

function toDraft (allocation: ILoanPaymentAllocation): IAllocationDraft {
  return {
    amount_to_interest: String(allocation.amount_to_interest),
    amount_to_principal: String(allocation.amount_to_principal),
    loan: allocation.loan,
  };
}

function allocationTotal (allocation: IAllocationDraft): number {
  return Number(allocation.amount_to_interest || 0) + Number(allocation.amount_to_principal || 0);
}

/**
 * What the rows come to. Rounded to the cent because summing floats off a form drifts -- 0.1 + 0.2
 * is not 0.3 -- and this figure is compared against the payment amount for exact equality.
 */
function draftTotal (allocations: IAllocationDraft[]): number {
  return round(allocations.reduce((total, allocation) => total + allocationTotal(allocation), 0), MONEY_DECIMAL_PLACES);
}

/**
 * Whether these rows can be saved against `amount`.
 *
 * The API refuses a payment whose allocations do not sum to it, so this is the same rule checked a
 * step earlier: the modal keeps the save button down rather than letting the round trip come back
 * with a validation error the user could have been shown while typing.
 */
function allocationsBalance (allocations: IAllocationDraft[], amount: string): boolean {
  const total = Number(amount || 0);

  return !!allocations.length && !!total && draftTotal(allocations) === round(total, MONEY_DECIMAL_PLACES);
}

/**
 * `closingLoanIds` is held by the modal rather than on the rows, because a refetch replaces every
 * row: the figures describe a split of an amount that has changed, but ticking Close is a decision
 * about the loan and outlives any of them. Ids for loans the current proposal does not reach are
 * simply not matched here, so nothing stale is ever posted.
 */
function toPayload (allocations: IAllocationDraft[], closingLoanIds: string[]): ILoanPaymentAllocation[] {
  return allocations
    .map(allocation => ({
      amount_to_interest: Number(allocation.amount_to_interest || 0),
      amount_to_principal: Number(allocation.amount_to_principal || 0),
      closes_loan: closingLoanIds.indexOf(allocation.loan) !== -1,
      loan: allocation.loan,
    }))
    // A loan allocated nothing is left off entirely rather than sent as a pair of zeroes: the API
    // takes one allocation per loan per payment, and a zero row is a row that says nothing -- unless
    // it closes the loan, which is a decision the row is carrying whatever the money says.
    .filter(allocation =>
      allocation.amount_to_interest || allocation.amount_to_principal || allocation.closes_loan);
}

export {
  IAllocationDraft,
  allocationTotal,
  allocationsBalance,
  draftTotal,
  toDraft,
  toPayload,
};
